import type { ReactNode } from "react";
import { rewardsErrorMessageKey } from "@ticketon/rewards-graphql";
import { Button, Skeleton, cn } from "ui";
import { AlertTriangle, Inbox } from "lucide-react";
import type { Translator } from "../types/nav";
import { useRewardsAdmin } from "../host/RewardsAdminHost";

export interface ILoadingStateProps {
  /** Optional message; defaults to a translated "loading" label. */
  message?: string;
  t?: Translator;
  className?: string;
}

/** Skeleton placeholder rows shown while a screen's primary query is in flight. */
export function LoadingState({ message, t: tOverride, className }: ILoadingStateProps) {
  const { t } = useRewardsAdmin({ t: tOverride });
  return (
    <div
      className={cn("flex flex-col gap-3 rounded-xl border border-gray-100 bg-white p-5 shadow-sm", className)}
      role="status"
      aria-live="polite"
      aria-label={message ?? t("rewards.state.loading")}
    >
      {Array.from({ length: 5 }).map((_, index) => (
        <div key={index} className="flex items-center gap-4">
          <Skeleton className="h-10 w-10 shrink-0 rounded-lg" />
          <Skeleton className="h-4 flex-1" />
          <Skeleton className="hidden h-4 w-24 sm:block" />
          <Skeleton className="hidden h-4 w-16 md:block" />
        </div>
      ))}
    </div>
  );
}

/** Props of the error panel; also the contract for a host-injected `ErrorState`. */
export interface IErrorStateProps {
  /** Already-translated message. */
  message?: string;
  /** Retry callback; renders a retry button when provided. */
  onRetry?: () => void;
  className?: string;
}

export interface IRewardsErrorStateProps extends IErrorStateProps {
  /** A rewards error (raw or normalized); rendered as `t(rewardsErrorMessageKey(error))`. */
  error?: unknown;
  t?: Translator;
}

/**
 * Error panel. Renders the host's `ErrorState` when one is injected, otherwise a
 * package fallback. A rewards `error` is translated through its error code, never
 * through the server message.
 */
export function ErrorState({ error, message, onRetry, className, t: tOverride }: IRewardsErrorStateProps) {
  const { t, ErrorState: HostErrorState } = useRewardsAdmin({ t: tOverride });
  const resolvedMessage = message ?? (error ? t(rewardsErrorMessageKey(error)) : t("rewards.state.error"));

  if (HostErrorState) {
    return <HostErrorState message={resolvedMessage} onRetry={onRetry} className={className} />;
  }

  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center gap-3 rounded-xl border border-ticketon-error/20 bg-ticketon-error-bg px-6 py-12 text-center",
        className,
      )}
      role="alert"
    >
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-white/70 text-ticketon-error-text">
        <AlertTriangle className="h-6 w-6" />
      </div>
      <p className="text-base font-medium text-ticketon-error-text">{resolvedMessage}</p>
      {onRetry && (
        <Button variant="outline" onClick={onRetry} data-testid="error-retry">
          {t("rewards.action.retry")}
        </Button>
      )}
    </div>
  );
}

export interface IEmptyStateProps {
  title?: string;
  description?: string;
  icon?: ReactNode;
  action?: ReactNode;
  t?: Translator;
  className?: string;
}

/** Empty placeholder shown when a list/query returns no results. */
export function EmptyState({ title, description, icon, action, t: tOverride, className }: IEmptyStateProps) {
  const { t } = useRewardsAdmin({ t: tOverride });
  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-gray-300 bg-white px-6 py-14 text-center",
        className,
      )}
    >
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gray-100 text-gray-400">
        {icon ?? <Inbox className="h-6 w-6" />}
      </div>
      <p className="mt-1 text-base font-medium text-ticketon-dark-gray">{title ?? t("rewards.state.empty")}</p>
      {description && <p className="max-w-md text-sm text-gray-500">{description}</p>}
      {action && <div className="mt-2">{action}</div>}
    </div>
  );
}

/** Translated message for a list error, for `CommonTable`'s `errorMessage`. */
export function useErrorMessage(error: unknown, tOverride?: Translator): string | undefined {
  const { t } = useRewardsAdmin({ t: tOverride });
  return error ? t(rewardsErrorMessageKey(error)) : undefined;
}
